/**
 * Upload flow logic: presign → S3 PUT → complete notification.
 * Spec: AGENT-SPEC-2ABC §4.3 — Track Upload
 *
 * Uses XMLHttpRequest for progress tracking (fetch doesn't support
 * upload progress events).
 */

import type {
  PresignResponse,
  CompleteUploadResponse,
} from '@regatta-replay/types';
import { api } from './api';
import { useAuthStore } from '../stores/auth';

// ── Types ──

export type UploadPhase = 'idle' | 'presigning' | 'uploading' | 'processing' | 'complete' | 'error';

export interface UploadState {
  file: File;
  uploadId?: string;
  s3Key?: string;
  phase: UploadPhase;
  progress: number; // 0-100 for upload phase
  error?: string;
}

export interface UploadOptions {
  regattaId?: string;
  boatId?: string;
  trackDate?: string;
  onStateChange: (state: UploadState) => void;
  signal?: AbortSignal;
}

// ── Constants ──

const VALID_EXTENSIONS = new Set(['vkx', 'vtk', 'gpx', 'usr']);
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

// ── Validation ──

export function validateFile(file: File): string | null {
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!ext || !VALID_EXTENSIONS.has(ext)) {
    return `Invalid file type ".${ext || ''}". Accepted: VKX, VTK, GPX, USR`;
  }
  if (file.size > MAX_FILE_SIZE) {
    return `File too large (${formatFileSize(file.size)}). Maximum: 100MB`;
  }
  if (file.size === 0) {
    return 'File is empty';
  }
  return null;
}

export function isValidExtension(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase();
  return ext != null && VALID_EXTENSIONS.has(ext);
}

// ── Upload Flow ──

export async function uploadTrackFile(
  file: File,
  options: UploadOptions
): Promise<CompleteUploadResponse> {
  const { regattaId, boatId, trackDate, onStateChange, signal } = options;

  const state: UploadState = {
    file,
    phase: 'presigning',
    progress: 0,
  };
  onStateChange({ ...state });

  // Step 1: Get presigned URL
  const presignBody: Record<string, unknown> = {
    fileName: file.name,
    fileType: file.type || 'application/octet-stream',
    fileSize: file.size,
  };
  if (regattaId) presignBody.regattaId = regattaId;

  const presign = await api.post<PresignResponse>('/uploads/presign', presignBody);

  state.uploadId = presign.uploadId;
  state.s3Key = presign.s3Key;
  state.phase = 'uploading';
  onStateChange({ ...state });

  // Step 2: Upload directly to S3 via presigned URL
  await uploadToS3(presign.presignedUrl, file, (progress) => {
    state.progress = progress;
    onStateChange({ ...state });
  }, signal);

  // Step 3: Notify server upload is complete
  state.phase = 'processing';
  state.progress = 100;
  onStateChange({ ...state });

  const completeBody: Record<string, unknown> = {
    fileName: file.name,
    s3Key: presign.s3Key,
    fileSize: file.size,
  };
  if (boatId) completeBody.boatId = boatId;
  if (regattaId) completeBody.regattaId = regattaId;
  if (trackDate) completeBody.trackDate = trackDate;

  const result = await api.post<CompleteUploadResponse>(
    `/uploads/${presign.uploadId}/complete`,
    completeBody,
  );

  // If server returned 202 (async processing), stay in 'processing' phase
  // The caller should use useUploadStatus hook to poll for completion
  if (result.status === 'pending' || result.status === 'processing') {
    state.phase = 'processing';
  } else {
    state.phase = 'complete';
  }
  onStateChange({ ...state });

  return result;
}

// ── S3 Upload with Progress ──

function uploadToS3(
  presignedUrl: string,
  file: File,
  onProgress: (percent: number) => void,
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelled'));
    });

    if (signal) {
      signal.addEventListener('abort', () => xhr.abort());
    }

    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    // Include auth token for MSW mock interception
    const token = useAuthStore.getState().token;
    if (token && presignedUrl.startsWith('/')) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.send(file);
  });
}

// ── Helpers ──

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function getFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() || '';
}
